import { useEffect, useState, useRef } from "react";
import { useSwipe } from "@/hooks/useSwipe";

export interface MediaItem {
  type: 'image' | 'video';
  url: string;
}

function getYouTubeEmbedUrl(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

function getVimeoEmbedUrl(url: string): string | null {
  const match = url.match(/vimeo\.com\/(\d+)/);
  return match ? `https://player.vimeo.com/video/${match[1]}` : null;
}

interface MediaCarouselProps {
  items: MediaItem[];
  carouselVisible: number;
  onItemClick: (index: number) => void;
  isPaused: boolean;
}

export default function MediaCarousel({ items, carouselVisible, onItemClick, isPaused }: MediaCarouselProps) {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [isTabVisible, setIsTabVisible] = useState(!document.hidden);

  const totalSlides = Math.max(1, items.length - carouselVisible + 1);
  const showNav = items.length > carouselVisible;
  const max = Math.max(0, items.length - carouselVisible);

  function next() {
    setCarouselIndex((prev) => Math.min(prev + 1, max));
  }

  function prev() {
    setCarouselIndex((prev) => Math.max(prev - 1, 0));
  }

  const swipe = useSwipe(next, prev);

  useEffect(() => {
    if (carouselRef.current) {
      const itemWidth = carouselRef.current.scrollWidth / items.length;
      carouselRef.current.scrollTo({ left: carouselIndex * itemWidth, behavior: "smooth" });
    }
  }, [carouselIndex, carouselVisible, items.length]);

  useEffect(() => {
    if (isPaused || isHovering || !isTabVisible) {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
        autoPlayRef.current = null;
      }
      return;
    }
    if (max <= 0) {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
        autoPlayRef.current = null;
      }
      return;
    }
    autoPlayRef.current = setInterval(() => {
      setCarouselIndex((prev) => (prev >= max ? 0 : prev + 1));
    }, 3000);
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
        autoPlayRef.current = null;
      }
    };
  }, [max, isPaused, isHovering, isTabVisible]);

  useEffect(() => {
    function handleVisibility() {
      setIsTabVisible(!document.hidden);
    }
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  if (items.length === 0) return null;

  return (
    <div>
      <div
        className="relative overflow-hidden rounded-xl"
        style={{ touchAction: "pan-y" }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        {...swipe.handlers}
      >
        <div
          ref={carouselRef}
          className="flex gap-3 md:gap-4 transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${carouselIndex * (100 / carouselVisible)}%)` }}
        >
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                if (swipe.arrastou.current) { swipe.arrastou.current = false; return; }
                onItemClick(index);
              }}
              className="group relative overflow-hidden rounded-xl cursor-pointer block shrink-0"
              style={{
                width: `calc(${100 / carouselVisible}% - ${carouselVisible > 1 ? (carouselVisible - 1) * 12 / carouselVisible : 0}px)`,
              }}
            >
              {item.type === 'video' ? (
                <div className="aspect-[4/5] w-full relative bg-black">
                  {getYouTubeEmbedUrl(item.url) ? (
                    <img
                      src={`https://img.youtube.com/vi/${item.url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1]}/0.jpg`}
                      alt={`Vídeo ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : getVimeoEmbedUrl(item.url) ? (
                    <div className="w-full h-full flex items-center justify-center bg-neutral-900">
                      <i className="ri-video-line text-4xl text-white/40" />
                    </div>
                  ) : (
                    // "#t=1.5" mostra um quadro do vídeo como capa: o primeiro quadro costuma ser preto (fade de entrada)
                    <video
                      src={`${item.url}#t=1.5`}
                      className="w-full h-full object-cover"
                      preload="metadata"
                      muted
                      playsInline
                    />
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/80">
                      <i className="ri-play-fill text-brown text-xl" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-[4/5] w-full overflow-hidden">
                  <img
                    src={item.url}
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80">
                      <i className="ri-zoom-in-line text-brown text-lg" />
                    </div>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Setas sobre as fotos, em qualquer tela (no computador não havia como passar para o lado) */}
        {showNav && carouselIndex > 0 && (
          <button
            onClick={prev}
            className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/90 text-brown shadow-md hover:bg-primary hover:text-white transition-all cursor-pointer z-10"
            aria-label="Anterior"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
        )}
        {showNav && carouselIndex < max && (
          <button
            onClick={next}
            className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-white/90 text-brown shadow-md hover:bg-primary hover:text-white transition-all cursor-pointer z-10"
            aria-label="Próximo"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        )}
      </div>

      {showNav && totalSlides > 1 && (
        <div className="flex items-center justify-center gap-2 mt-5">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={() => setCarouselIndex(i)}
              className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                i === carouselIndex ? "bg-primary w-6" : "bg-primary/30 hover:bg-primary/50"
              }`}
              aria-label={`Ir para slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}